import React from 'react';
import { Helmet } from 'react-helmet-async';

const Section = ({ title, children }) => (
  <div style={{ background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', padding: '28px 36px', marginBottom: 20, backdropFilter: 'blur(8px)' }}>
    <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700, marginBottom: 14, color: 'var(--t-primary)', letterSpacing: '-0.015em' }}>{title}</h2>
    {children}
  </div>
);

const P = ({ children }) => (
  <p style={{ color: 'var(--t-secondary)', lineHeight: 1.8, marginBottom: 12, fontSize: '0.93rem' }}>{children}</p>
);

const UL = ({ items }) => (
  <ul style={{ color: 'var(--t-secondary)', lineHeight: 1.9, paddingLeft: '1.5rem', fontSize: '0.92rem', marginBottom: 12 }}>
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

export default function PrivacyPolicy() {
  const updated = 'July 2026';
  return (
    <div>
      
      <Helmet>
  <title>Privacy Policy — ILoveConvert</title>
  <meta name="description" content="ILoveConvert privacy policy. Your files are secure and automatically deleted after 2 hours." />
  <link rel="canonical" href="https://iloveconvert.in/privacy" />
  <meta property="og:title" content="Privacy Policy — ILoveConvert" />
  <meta property="og:description" content="ILoveConvert privacy policy. Your files are secure and automatically deleted." />
  <meta property="og:url" content="https://iloveconvert.in/privacy" />
  <meta property="og:type" content="website" />
  <meta name="twitter:card" content="summary" />
  <meta name="twitter:title" content="Privacy Policy — ILoveConvert" />
</Helmet>

      <section className="hero" style={{ padding: '56px 28px 40px' }}>
        <div className="hero-inner">
          <h1 style={{ fontSize: 'clamp(1.8rem,4vw,2.8rem)' }}>Privacy <span className="accent">Policy</span></h1>
          <p style={{ color: 'var(--t-secondary)' }}>Last updated: {updated}</p>
        </div>
      </section>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 28px 80px' }}>

        <Section title="1. Introduction">
          <P>ILoveConvert ("we", "our", or "us") operates the website iloveconvert.in (the "Service"). This Privacy Policy explains how we collect, use, and protect information when you use our file conversion tools and related services.</P>
          <P>By using ILoveConvert, you agree to the collection and use of information in accordance with this policy. We are committed to protecting your privacy and handling your data transparently.</P>
        </Section>

        <Section title="2. Information We Collect">
          <P><strong style={{ color: 'var(--t-primary)' }}>Files you upload:</strong> When you use our conversion tools, you upload files to our servers. These files are used solely to perform the conversion you requested. We do not read, analyze, sell, or retain your file contents for any other purpose.</P>
          <P><strong style={{ color: 'var(--t-primary)' }}>Usage data:</strong> We collect anonymized usage information including pages visited, tools used, browser type, device type, and general geographic region. This data helps us improve our service and is not linked to individual users.</P>
          <P><strong style={{ color: 'var(--t-primary)' }}>Account information (optional):</strong> If you create an account, we collect your email address and a hashed password. Account creation is entirely optional — all tools work without registration.</P>
          <P><strong style={{ color: 'var(--t-primary)' }}>Cookies and local storage:</strong> We use cookies and local storage to remember your session and preferences. See Section 6 for more details.</P>
          <P><strong style={{ color: 'var(--t-primary)' }}>Server logs:</strong> Like virtually all websites, our servers automatically log technical information for every request — including your IP address, browser and device type, referring page, and timestamp. This is standard technical logging used for security, abuse prevention, and troubleshooting; it is not used to build an individual profile of you.</P>
        </Section>

        <Section title="3. How We Use Your Files">
          <UL items={[
            'Files you upload are used only to perform the conversion, merge, split, compress, or background removal you requested',
            'The conversion itself — including the AI background removal model — runs on our own servers, not a third-party API',
            'Uploaded input files are deleted from our servers as soon as your conversion finishes, not kept around for hours',
            'The converted output file is stored via Cloudinary, a third-party cloud storage provider, so your download link stays reliable — it is automatically deleted after 2 hours',
            'Cloudinary only stores and delivers the file; it does not read, analyze, or index its contents for its own purposes',
            'We do not use your files to train AI models',
          ]} />
        </Section>

        <Section title="4. Third-Party Service Providers">
          <P>We rely on a small number of third-party providers to operate ILoveConvert. Each one only receives the data it needs to perform its specific function:</P>
          <UL items={[
            'Hosting & infrastructure: Hostinger (web and application hosting) and MongoDB Atlas (database hosting for account and conversion records)',
            'File storage & delivery: Cloudinary (temporarily stores and delivers converted output files — see Section 3)',
            'Analytics: Google Analytics (helps us understand how the site is used — see Section 6)',
            'Advertising: Google AdSense (see Section 5 below for full details)',
          ]} />
          <P>We do not sell your personal data or file contents to any third party.</P>
        </Section>

        <Section title="5. Google AdSense and Third-Party Advertising">
          <P>ILoveConvert intends to display advertisements served by Google LLC through the Google AdSense program to help keep our tools free of charge. This section describes how that advertising works once enabled; it applies from the point AdSense is actually active on the site.</P>
          <P><strong style={{ color: 'var(--t-primary)' }}>How Google AdSense works:</strong> When active, Google uses cookies and similar tracking technologies to serve ads based on your prior visits to this website and other websites. Google's use of advertising cookies enables it and its partners to serve ads to you based on your visit to our site and/or other sites on the internet.</P>
          <P><strong style={{ color: 'var(--t-primary)' }}>Opting out:</strong> You may opt out of personalised advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noreferrer" style={{ color: 'var(--red)' }}>Google Ads Settings</a>. You can also opt out of third-party vendors' use of cookies by visiting <a href="https://www.aboutads.info/choices/" target="_blank" rel="noreferrer" style={{ color: 'var(--red)' }}>aboutads.info</a>.</P>
          <P>For more information about Google's privacy practices, please visit <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--red)' }}>Google's Privacy Policy</a>.</P>
        </Section>

        <Section title="6. Cookies">
          <P>We use the following types of cookies:</P>
          <UL items={[
            'Essential cookies: Required for the website to function. Includes session management and security tokens.',
            'Preference cookies: Remember your settings such as chosen output formats and quality preferences.',
            'Analytics cookies: Placed by Google Analytics to help us understand how visitors use our website.',
            'Advertising cookies: Placed by Google AdSense to serve relevant advertisements. These may track your browsing activity across other websites.',
          ]} />
          <P>You can control cookies through your browser settings. Disabling certain cookies may affect the functionality of our service.</P>
        </Section>

        <Section title="7. Data Retention">
          <UL items={[
            'Uploaded files: Deleted immediately once your conversion completes — typically within seconds, not hours',
            'Converted files: Stored via Cloudinary and deleted within 2 hours of creation',
            'Account data: Retained until you delete your account',
            'Server logs: Retained for a limited period for security and troubleshooting purposes',
          ]} />
        </Section>

        <Section title="8. Data Security">
          <P>We implement appropriate technical and organisational measures to protect your files and personal information against unauthorised access, alteration, disclosure, or destruction. These include:</P>
          <UL items={[
            'HTTPS encryption for all data transmitted to and from our servers',
            'Secure server infrastructure with regular security updates',
            'Automatic file deletion after the retention period',
            'Access controls limiting who can access server infrastructure',
          ]} />
        </Section>

        <Section title="9. Children's Privacy">
          <P>ILoveConvert is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us and we will delete it promptly.</P>
        </Section>

        <Section title="10. Changes to This Policy">
          <P>We may update this Privacy Policy from time to time. We will notify you of any significant changes by updating the "Last updated" date at the top of this page. We encourage you to review this policy periodically.</P>
        </Section>

        <Section title="11. Contact Us">
          <P>If you have any questions about this Privacy Policy or our data practices, please contact us:</P>
          <P>📧 Email: <a href="mailto:support@iloveconvert.in" style={{ color: 'var(--red)' }}>support@iloveconvert.in</a><br />🌐 Website: iloveconvert.in</P>
        </Section>

     
      </div>
    </div>
  );
}
